import {
    Box,
    Center,
    Flex,
    Heading,
    HStack,
    Text,
    VStack,
} from '@chakra-ui/react'
import MdxViewer from '@kalvi-education/mdx-viewer'

import { zeroImportant } from '../chakraTheme'
import {
    Answered,
    ReviewAnswered,
    ReviewUnAnswered,
    UnAnswered,
} from '../components/Icons/axInstructions'
import { DotLottiePlayer } from '@dotlottie/react-player'
import PROCTORING_TYPE from '@/enums/proctoringType'

const QuestionPalette = () => {
    return (
        <Box mt={12}>
            <Heading fontWeight={'bold'} fontSize={'18px'}>
                Question Palette
            </Heading>
            <Text mt={2}>
                The question palette displayed on the left side of the
                assessment screen will show the following statuses depicted by
                distinct symbols.
            </Text>
            <HStack align={'baseline'} mt={4}>
                <VStack align={'baseline'} gap={9}>
                    <QuestionStatus
                        icon={<Answered w={'48px'} h={'48px'} />}
                        label={'Answered'}
                    />
                    <QuestionStatus
                        icon={<UnAnswered w={'48px'} h={'48px'} />}
                        label={'Unanswered'}
                    />
                </VStack>
                <VStack
                    align={'baseline'}
                    marginInlineStart={zeroImportant}
                    pl={12}
                    gap={9}
                >
                    <QuestionStatus
                        icon={<ReviewAnswered width="48px" height="48px" />}
                        label={'Marked for review but answered'}
                    />

                    <QuestionStatus
                        icon={<ReviewUnAnswered width="48px" height="48px" />}
                        label={'Marked for review but unanswered'}
                    />
                </VStack>
            </HStack>
        </Box>
    )
}

const MarkingScheme = () => {
    return (
        <Box mt={12}>
            <Heading fontWeight={'bold'} fontSize={'18px'}>
                Marking Scheme
            </Heading>
            <Text mt={2}>
                Refer to the top right of each question for the marks awarded
                for a correct answer or deducted for an incorrect answer as
                shown below.
            </Text>
            <Flex mt={4}>
                <MarkingSchemeBox
                    bgColor={'zanah'}
                    content={'+X'}
                    label={'Correct'}
                    color={'fruitsalad'}
                />
                <MarkingSchemeBox
                    bgColor={'cinderella'}
                    content={'-Y'}
                    label={'Incorrect'}
                    color={'flushmahogany'}
                />
            </Flex>
        </Box>
    )
}

const ProctoringInstructions = () => {
    return (
        <div className="border-2 border-dashed border-[#EFB100] bg-[#fdf8ed] p-6 mt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                    <h2 className="text-lg font-bold text-gray-900 mb-6">Proctoring Guidelines</h2>

                    <ul className="text-gray-700">
                        <li className="flex items-start gap-2">
                            <span className="text-base font-bold mt-1">•</span>
                            <span className="text-base">
                                This assessment requires you to share your Camera and Microphone feed, as well as your entire screen.
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-base font-bold mt-1">•</span>
                            <span className="text-base">
                                Once the assessment begins, make sure that your Camera & Microphone feed, along with Screen Sharing, are
                                clearly visible in the top-left corner of the assessment screen. If they appear blank or the feed is
                                incorrect, contact your invigilator right away. Failure to do so will void your chances for any further
                                consideration of retake/reevaluation.
                            </span>
                        </li>
                    </ul>
                </div>
                <div className="border-2 border-[#EDEDED] md:col-span-1 flex justify-center items-center mt-12">
                    <div>
                        <DotLottiePlayer
                            src="/animations/assessment-proctoring-instructions.lottie"
                            autoplay
                            loop
                            style={{ height: '128px' }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}


const MarkingSchemeBox = ({ color, bgColor, content, label }) => {
    return (
        <Box mr={'18px'}>
            <Center w={'48px'} h={'24px'} bg={bgColor}>
                <Heading color={color} fontSize={'md'}>
                    {content}
                </Heading>
            </Center>
            <Text
                fontStyle={'italic'}
                color={'scorpion'}
                fontWeight={'normal'}
                fontSize={'xs'}
                mt={1}
            >
                {label}
            </Text>
        </Box>
    )
}

const QuestionStatus = ({ icon, label, ...props }) => {
    return (
        <Flex align={'center'} {...props} mt={zeroImportant}>
            {icon}
            <Text ml={6}>{label}</Text>
        </Flex>
    )
}

const Content = ({ customInstructions, customSubtext, proctoringType }) => {
    let instructions = customInstructions
        ? customInstructions
        : '- This assessment can be attempted only ONCE. Hence, please ensure you are seated in a distraction-free environment.\n- Please ensure you are connected to a strong wifi/ethernet network.\n- In case of internet discrepancies, your timer will still keep running. However, you can continue attempting the current question.\n- The security code will be provided by the invigilator at your venue.\n- In case of any technical difficulties, please reach out to the invigilator.\n- Give your best. Good luck!'
    let subtext =
        customSubtext ??
        'Please read the below instructions carefully and begin the assessment.'
    return (
        <Flex flexDirection={'column'} mb={12}>
            <Heading fontSize={'2xl'} fontWeight={'extrabold'}>
                Instructions
            </Heading>
            <Text color="scorpion2" mt={2}>
                {subtext}
            </Text>
            <Flex flexDirection="column" mt={8}>
                <MdxViewer mdx={instructions} />
            </Flex>
             {proctoringType === PROCTORING_TYPE.REMOTE_PROCTORING && import.meta.env.VITE_IS_PROCTORING_V2_ENABLED === "yes" && 
                  <ProctoringInstructions />
             }
            {/* Marking Scheme */}
            <MarkingScheme />
            <QuestionPalette />
        </Flex>
    )
}

export default Content
